import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import type { AfterViewInit, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { IonInput, IonSearchbar, ModalController, PopoverController } from '@ionic/angular';
import { Organization } from 'src/models/organization';
import { SearchService } from 'src/services/search.service';

@Component({
  selector: 'app-typeahead',
  templateUrl: './type-ahead.component.html',
  styleUrls: ['./type-ahead.component.scss'],
})
export class TypeAheadComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() items: any[] = [];
  @Input() selectedItems: any[] = [];
  @Input() title = 'Select Items';
  @Input() dynamicSearchMode?: 'accounts' | 'horses' | 'judges';
  @Input() allowMultiple = true;
  @Input() usePopover = false;
  @Input() showCreateNewItem = false;

  @Input() displayExtraContent = false;

  @Output() selectionCancel = new EventEmitter<void>();
  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() addNew = new EventEmitter<boolean>();

  @ViewChild('searchbar') searchbar: IonSearchbar | IonInput;

  filteredItems: any[] = [];
  workingSelectedValues: any[] = [];
  isLoading = false;

  constructor(
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private searchService: SearchService,
  ) { }

  ngOnInit() {
    this.filteredItems = [...this.items];
    this.workingSelectedValues = [...this.selectedItems];
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.searchbar.setFocus();
    }, 250);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // NOTE: Fixed 'items' not filtering....cannot assign filtered items to items being passed in because filteredItems is always displayed...so not filtering takes effect

    if (changes['items']?.currentValue) {
      this.items = [...this.items]; 
    }
    if (changes['selectedItems']) {
      this.workingSelectedValues = [...this.selectedItems];
    }
  }

  trackItems(index: number, item: Organization) {
    return item.id;
  }

  cancelChanges() {
    this.selectionCancel.emit();
    this.close();
  }

  confirmChanges() {
    // console.log(this.workingSelectedValues);
    this.selectionChange.emit(this.workingSelectedValues);
    this.close();
  }

  addNewItem() {
    this.addNew.emit(true);
    this.close();
  }

  async searchbarInput(ev: any) {
    const searchQuery = ev.target.value;
    if (this.dynamicSearchMode) {
      if (searchQuery && searchQuery.length > 1) {
        this.isLoading = true;
        if (this.dynamicSearchMode == 'accounts') {
          let accounts = await this.searchService.searchAccounts(searchQuery);

          // console.log('search results', accounts);
          //format accounts to display correctly. adding full account obj to formatted obj
          this.filteredItems = accounts.map((account) => {
            return {
              id: account.id,
              name: `${account.firstName} ${account.lastName} (${account.city}, ${account.state})`,
              account: account,
            };
          });
        } else if (this.dynamicSearchMode == 'horses') {
          const horses = await this.searchService.searchHorses(searchQuery);
          console.log(horses);
          this.filteredItems = horses;
        }
        else if (this.dynamicSearchMode == 'judges') {
          const accounts = await this.searchService.searchAccounts(searchQuery);
          //format accounts to display correctly. adding full account obj to formatted obj
          this.filteredItems = accounts.map((account) => {
            return {
              id: account.id,
              name: `${account.firstName} ${account.lastName} (${account.id}, ${account.emailAddress})`,
              account: account,
            };
          });
        }
        this.isLoading = false;
      } else {
        this.filteredItems = [];
      }
    } else {
      this.filterList(searchQuery);
    }
  }

  /**
   * Update the rendered view with
   * the provided search query. If no
   * query is provided, all data
   * will be rendered.
   */
  filterList(searchQuery: string | undefined) {
    /**
     * If no search query is defined,
     * return all options.
     */

    if (searchQuery === undefined) {
      this.filteredItems = [...this.items];
    } else {
      /**
       * Otherwise, normalize the search
       * query and check to see which items
       * contain the search query as a substring.
       */
      const normalizedQuery = searchQuery.toLowerCase();
      this.filteredItems = this.items.filter((item) => {
        return (item.name || item.displayName).toLowerCase().includes(normalizedQuery);
      });
    }
  }

  isChecked(id: number) {
    return this.workingSelectedValues.find((item) => item.id === id);
  }

  checkboxChange(ev: any) {
    const { checked, value } = ev.detail;
    // console.log(checked, value);

    if (!this.allowMultiple) {
      this.workingSelectedValues = [value];
      this.confirmChanges();
    } else {
      if (checked && !this.workingSelectedValues.some((x) => x.id === value.id)) {
        this.workingSelectedValues.push(value);
      } else {
        this.workingSelectedValues = this.workingSelectedValues.filter(x => x.id !== value.id);
      }
    }
  }

  private close() {
    if (this.usePopover) {
      this.popoverCtrl.dismiss();
    } else {
      this.modalCtrl.dismiss();
    }
  }
}
